import React, { Suspense, useEffect, useState } from 'react'
import { ConfigProvider, Layout, Tour } from 'antd'
import { observer } from 'mobx-react-lite'
import '../../../design/App.css' // TODO: Integrate in scss
import '../../../design/compiled-sass.css'

// Stores
import rootStore from '../../../stores'
import { MobXProvider } from '../../../cls/stores/MobXProvider'

// Design
import AppDesign from '../../../design/AppDesign'
import { layoutStyle } from '../../../design/app-style-config'

// App components
import Sidebar from '../sidebar'
import MainHeader from '../header/Header'
import MainContent from './Content'
import MainFooter from './Footer'
import { APP_TYPE } from '../../../config'
import CookieConsentPopover from '../../Form/CookieConsentPopover'
import { translate, TranslationProvider } from '../../../cls/TranslationContext'

import '../../../config/i18n' // Initialize translations
import { smartT } from '../../../config/translationGlobal'
import { useInViewAnimations } from '../../../js/useInViewAnimation'

/**
 * Component that serves as a fallback UI during lazy loading.
 * @returns {JSX.Element} The fallback UI.
 */
const WaitFallback = () => {
	return <div>{translate('single.please-wait')}</div> // Todo: translate
}

/**
 * The main App component that contains the layout and routing for the application.
 * @returns {JSX.Element} The App component.
 */
function App() {
	// ✅ Call hook at top-level (inside Router, thanks to index.js)
	useInViewAnimations({
		rootMargin: '0px 0px -10% 0px',
		threshold: 0.15,
		once: true
	})

	// Sidebar collapsed state
	const [collapsed, setCollapsed] = useState(
		true
		// isMobile ? true : false
	)

	// State variable to manage the visibility of the cookie consent popover
	const [cookiePopoverVisible, setCookiePopoverVisible] = useState(false)

	// Determine if the cookie consent popover should be shown based on the root store's state
	const shouldShowCookiePopover = rootStore.shouldShowCookiePopover()

	// update the popover visibility whenever the shouldShowCookiePopover changes
	useEffect(() => {
		setCookiePopoverVisible(shouldShowCookiePopover)
	}, [shouldShowCookiePopover])

	useEffect(() => {
		if (!rootStore.tourStore.open) return

		const script = document.createElement('script')
		script.textContent = `
			(function() {
				// console.log('[Tour Patch] Tour patch script loaded.');
				let previousInset = '';
				let previousTitle = '';
				let changeCount = 0;

				const interval = setInterval(() => {
					const tour = document.querySelector('.ant-tour');
					const titleEl = document.querySelector('.ant-tour-title');

					if (!tour || !titleEl || !tour.style || !tour.style.inset) return;

					const currentInset = tour.style.inset;
					const currentTitle = titleEl.textContent || '';

					if (previousTitle === currentTitle) {
						if (previousInset && currentInset !== previousInset) {
							changeCount++;

							const parts = currentInset.split(' ');
							if (parts.length === 4) {
								const right = parseFloat(parts[3]);
								if (!isNaN(right)) {
									const newRight = Math.max(0, right - 10) + 'px';
									parts[3] = newRight;
									const newInset = parts.join(' ');
									tour.style.inset = newInset;

									// 👇 Treat as if we're in a new state now
									previousInset = newInset;
								}
							}
						} else {
							// console.log('[Tour Patch] No flicker detected for same title');
						}
					} else {
						// console.log('[Tour Patch] New step detected, skipping inset check');
					}

					previousInset = tour.style.inset;
					previousTitle = currentTitle;
				}, 100);

				const stopCheck = setInterval(() => {
					if (!${rootStore.tourStore.open}) {
						clearInterval(interval);
						clearInterval(stopCheck);
					}
				}, 500);
			})();
		`
		document.body.appendChild(script)

		return () => {
			document.body.removeChild(script)
		}
	}, [rootStore.tourStore.open])

	// Set the app type if it is not already set
	if (!rootStore.appType.type) {
		// Determine the app type based on the hostname
		const hostname = window.location.hostname

		if (hostname === APP_TYPE.client) {
			rootStore.appType.setAppType('client')
		} else if (hostname === APP_TYPE.team) {
			rootStore.appType.setAppType('team')
		} else {
			console.warn('hostname:', hostname, 'app type defined:: client')
			rootStore.appType.setAppType('client') // Default to client
		}
	}

	return (
		<TranslationProvider>
			<Suspense fallback={<WaitFallback />}>
				<MobXProvider rootStore={rootStore}>
					<CookieConsentPopover
						visible={cookiePopoverVisible}
						setVisible={setCookiePopoverVisible}
					/>
					<AppDesign>
						<Layout id="app-layout" style={layoutStyle}>
							<Sidebar
								collapsed={collapsed}
								setCollapsed={setCollapsed}
							/>
							<Layout>
								<MainHeader
									collapsed={collapsed}
									setCollapsed={setCollapsed}
								/>
								<MainContent />
								<MainFooter />
							</Layout>
						</Layout>
						<ConfigProvider
							locale={{
								Tour: {
									Next: smartT('single.next'),
									Previous: smartT('single.previous'),
									Finish: smartT('single.finish')
								}
							}}
						>
							<Tour
								open={rootStore.tourStore.open}
								onClose={() => rootStore.tourStore.resetTour()}
								steps={rootStore.tourStore.steps}
							/>
						</ConfigProvider>
					</AppDesign>
				</MobXProvider>
			</Suspense>
		</TranslationProvider>
	)
}

export default observer(App)
